// Las guardas de los caminos que ponen categoría (grupo G, entrega 1: D1-7), del lado de la
// pantalla.
//
// El backend se niega ahora, con `{code, message}`, a clasificar algo que no se debe:
//
// - `categoria_no_es_hoja` (422): la categoría tiene subcategorías;
// - `movimiento_dividido` (409): un padre dividido tiene su categoría en cada parte;
// - `rango_bloqueado` (409): una proyección con el período traído de la real, sin el permiso
//   `projections.edit_locked`. En bloque trae además `count`, y no escribe nada del lote.
//
// Y clasificar en bloque ya no escribe a ciegas: salta y cuenta lo dividido, lo bloqueado (solo
// el chat), lo que cambió entre la lectura y la escritura, y confirma lo que ya estaba en esa
// categoría. `modified_count` cuenta solo los cambios de categoría escritos.
//
// Antes la pantalla anunciaba «N transacciones clasificadas» con N = lo seleccionado, aunque se
// hubieran omitido filas, y ponía todo en `manual` de forma optimista. Ahora anuncia lo que el
// servidor dice que escribió, y una línea por cada omisión.
//
// Devuelve textos ya elegidos con `t`, con sus claves escritas literales para que `check-i18n`
// las vea. Un backend anterior no manda los contadores nuevos: se leen como 0 y no aparecen.

function numero(v) {
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

/** El estado, el `code` y el `count` de un error de axios (el `detail` de los 409 y 422). */
function leer(error) {
  const status = error?.response?.status ?? null;
  const detail = error?.response?.data?.detail;
  const objeto = detail && typeof detail === "object" && !Array.isArray(detail);
  return {
    status,
    code: objeto && typeof detail.code === "string" ? detail.code : null,
    count: objeto && typeof detail.count === "number" && Number.isFinite(detail.count)
      ? detail.count : null,
  };
}

/**
 * Si la fila ofrece «Clasificar/Reclasificar»: no en un padre dividido, con la misma condición
 * que «Dejar Pendiente» (su categoría está en las partes, y el backend contesta 409
 * `movimiento_dividido`). Un padre que reclamó el diálogo (`is_split: false`, sin partes) sí.
 */
export function puedeClasificar(txn) {
  if (!txn || typeof txn !== "object") return false;
  const conPartes = Array.isArray(txn.splits) && txn.splits.length > 0;
  return !conPartes && txn.is_split !== true;
}

/** Si un error es el 409 del rango bloqueado (`rango_bloqueado`). */
export function esRangoBloqueado(error) {
  const { status, code } = leer(error);
  return status === 409 && code === "rango_bloqueado";
}

/**
 * El texto de un error al clasificar (o al editar, o al dejar pendiente con
 * `dejarPendiente: true`), por su `code`. `fallback` es el texto ya traducido de cada camino.
 */
export function textoDeErrorAlClasificar(error, t, { fallback, dejarPendiente = false } = {}) {
  const { status, code } = leer(error);
  if (status === 409 && code === "rango_bloqueado") return t("transactions.lockedRangeError");
  if (status === 422 && code === "categoria_no_es_hoja") return t("transactions.classifyNotLeafError");
  if (status === 409 && code === "movimiento_dividido") {
    return dejarPendiente
      ? t("transactions.unclassifyDividedError")
      : t("transactions.classifyDividedError");
  }
  if (status === 409 && code === "cambio_mientras") return t("transactions.changedMeanwhile");
  return fallback;
}

/**
 * El texto de un Confirmar del chat que el backend rechazó, por su `code` (el cuerpo ya leído de
 * `fetch`), o `null` si no es una de las guardas de clasificar. El caso: la categoría ganó
 * subcategorías entre la tarjeta y el Confirmar (422 `categoria_no_es_hoja`); un error genérico
 * no dice qué hacer.
 */
export function textoDeErrorDelChat(status, cuerpo, t) {
  return textoDeErrorAlClasificar({ response: { status, data: cuerpo } }, t, { fallback: null });
}

/**
 * Lo que dijeron los lotes de bulk-classify (de a 500), sumado. `escritas` es la suma de
 * `modified_count`; si algún lote no lo trae, se cuenta por sus `eventos` (lo que el servidor
 * auditó como escrito), y si tampoco, `null`: no se sabe, y no se anuncia un número.
 * `conContadores` dice si el backend manda los contadores nuevos (`omitidas`).
 */
export function sumarLotes(respuestas) {
  const r = {
    escritas: 0, dividido: 0, rango_bloqueado: 0, cambio_mientras: 0, confirmadas: 0,
    skipped_pendiente: 0, skipped_devuelto: 0, conContadores: false,
  };
  for (const d of respuestas || []) {
    if (typeof d?.modified_count === "number" && Number.isFinite(d.modified_count)) {
      if (r.escritas !== null) r.escritas += d.modified_count;
    } else if (Array.isArray(d?.eventos)) {
      if (r.escritas !== null) r.escritas += d.eventos.length;
    } else {
      r.escritas = null;
    }
    const om = d?.omitidas;
    if (om && typeof om === "object") {
      r.conContadores = true;
      r.dividido += numero(om.dividido);
      r.rango_bloqueado += numero(om.rango_bloqueado);
      r.cambio_mientras += numero(om.cambio_mientras);
    }
    // Lo que ya estaba en esa categoría: confirmado ahora (`confirmadas_sin_cambio`) o ya
    // confirmado de antes (`ya_estaban`, sin escribir). Las dos quedan confirmadas.
    r.confirmadas += numero(d?.confirmadas_sin_cambio);
    r.confirmadas += Array.isArray(d?.ya_estaban) ? d.ya_estaban.length : numero(d?.ya_estaban);
    r.skipped_pendiente += numero(d?.skipped_pendiente);
    r.skipped_devuelto += numero(d?.skipped_devuelto);
  }
  return r;
}

/** Las líneas de lo que no se clasificó, o se confirmó sin cambiar, cada una con su plural. */
export function lineasDeOmisiones(resumen, t) {
  const lineas = [];
  if (resumen.confirmadas > 0) {
    lineas.push(t("transactions.bulkClassifyAlreadyThere", { count: resumen.confirmadas }));
  }
  if (resumen.dividido > 0) {
    lineas.push(t("transactions.bulkClassifySkippedDivided", { count: resumen.dividido }));
  }
  if (resumen.rango_bloqueado > 0) {
    lineas.push(t("transactions.bulkClassifySkippedLocked", { count: resumen.rango_bloqueado }));
  }
  if (resumen.cambio_mientras > 0) {
    lineas.push(t("transactions.bulkClassifyChangedMeanwhile", { count: resumen.cambio_mientras }));
  }
  if (resumen.skipped_pendiente > 0) {
    lineas.push(t("rules.toasts.skippedPart", { count: resumen.skipped_pendiente }));
  }
  if (resumen.skipped_devuelto > 0) {
    lineas.push(t("rules.toasts.skippedReturnedPart", { count: resumen.skipped_devuelto }));
  }
  return lineas;
}

/**
 * El aviso de un Clasificar en bloque que terminó: `{tipo, texto}`. Con algo escrito, `success`
 * con «N clasificadas» (en plural) y las omisiones; con 0 escritas, `info` con las omisiones y
 * nunca «0 transacciones clasificadas». `null` si no hay nada que decir (0 escritas y ninguna
 * omisión: la lista recargada lo muestra).
 */
export function avisoDeClasificarEnBloque(resumen, t) {
  const omisiones = lineasDeOmisiones(resumen, t);
  if (resumen.escritas === null) {
    return { tipo: omisiones.length ? "info" : "success",
      texto: [t("success.classified"), ...omisiones].join(" · ") };
  }
  if (resumen.escritas > 0) {
    return { tipo: "success",
      texto: [t("transactions.bulkClassifySuccess", { count: resumen.escritas }), ...omisiones].join(" · ") };
  }
  if (omisiones.length === 0) return null;
  return { tipo: "info", texto: omisiones.join(" · ") };
}

/**
 * El aviso de un «Confirmar IA» en bloque que terminó (grupo G, entrega 2): `{tipo, texto}`,
 * nunca `null`. `confirmadas` es la suma de `confirmed_count` de los lotes y `cambiaron` la de
 * `omitidas.cambio_mientras` (lo que dejó de ser «IA por confirmar» entre la lectura y la
 * escritura, y el servidor no tocó). Antes decía «Clasificación confirmada» con una o con 500:
 * con «Seleccionar todo lo filtrado» el botón no muestra número, y el aviso es lo único que dice
 * cuántas se confirmaron.
 *
 * - confirmadas > 0: `success` con «N clasificaciones de la IA confirmadas» y, si alguna cambió,
 *   la línea de «cambiaron mientras tanto»;
 * - 0 confirmadas y alguna cambió: `info` solo con esa línea (decir «no hay pendientes» sería
 *   falso: las había, y cambiaron);
 * - 0 y 0: `info` con «No hay transacciones con IA pendiente de confirmar en la selección».
 */
export function avisoDeConfirmarIa(confirmadas, cambiaron, t) {
  const n = numero(confirmadas);
  const m = numero(cambiaron);
  if (n > 0) {
    return { tipo: "success", texto: [
      t("transactions.bulkConfirmAiSuccess", { count: n }),
      ...(m > 0 ? [t("transactions.bulkClassifyChangedMeanwhile", { count: m })] : []),
    ].join(" · ") };
  }
  if (m > 0) {
    return { tipo: "info", texto: t("transactions.bulkClassifyChangedMeanwhile", { count: m }) };
  }
  return { tipo: "info", texto: t("transactionsV2.noAiPending") };
}

/**
 * El aviso de un Clasificar en bloque que se detuvo en un lote por el rango bloqueado (409
 * `rango_bloqueado` con `count`). En el primer lote no se escribió nada: «este grupo no se
 * clasificó». Si ya se habían escrito lotes, cuántas quedaron (`done`, la suma de
 * `modified_count`) y que el resto no. Sin `count` (no debería pasar), el texto sin número.
 */
export function textoDeLoteDetenido(error, respuestas, t) {
  const { count } = leer(error);
  if (count === null) return t("transactions.lockedRangeError");
  const lotes = Array.isArray(respuestas) ? respuestas : [];
  if (lotes.length === 0) return t("transactions.lockedRangeBulkError", { count });
  const { escritas } = sumarLotes(lotes);
  if (escritas === null) return t("transactions.lockedRangeError");
  return t("transactions.lockedRangeBulkStopped", { done: escritas, count });
}

/**
 * El texto de la tarjeta del chat después de ejecutar un Clasificar: con los contadores de
 * bulk-classify (un backend con `omitidas`), armado con las claves; sin ellos (un backend
 * anterior, u otra acción como crear una regla), el `message` del servidor.
 */
export function textoDeTarjetaDelChat(resultado, t) {
  if (!resultado || typeof resultado !== "object") return undefined;
  if (!resultado.omitidas || typeof resultado.omitidas !== "object") return resultado.message;
  const aviso = avisoDeClasificarEnBloque(sumarLotes([resultado]), t);
  return aviso ? aviso.texto : resultado.message;
}
